import React, { useState } from 'react';
import * as Prism from 'prismjs';
if (typeof window !== 'undefined' && typeof window.Prism === 'undefined') {
  /* istanbul ignore next */
  window.Prism = Prism;
}
import { styled, useStyletron, type Theme as ThemeT } from 'baseui';
import type { StyleObject } from 'styletron-react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import LexicalAutoLinkPlugin from './plugins/AutoLinkPlugin';
import useEditorConfig from './hook/useEditorConfig';
import CustomOnChangePlugin from './plugins/CustomOnChangePlugin';
import OnBlurPlugin from './plugins/OnBlurPlugin';
import CodeHighlightPlugin from './plugins/CodeHighlightPlugin';
import LinkEditorPlugin from './plugins/LinkEditorPlugin';
import EquationsPlugin from './plugins/EquationsPlugin';
import { validateUrl } from './utils/utils';
import { EDITOR_TRANSFORMER } from './utils/utils';
import { useLoadStyles } from '../../hook/useLoadStyles';
import CodeLanguagePlugin from './plugins/CodeLanguagePlugin';
import { MetaDetailsPlugin } from './plugins/MetaDetailsPlugin/meta-details-plugin';
import TextSyncPlugin from './plugins/TextSyncPlugin';
import PastePlugin from './plugins/PastePlugin';
import ReadOnlyContentEditable from './readonly-content-editable';
type TextEventHandlerT = ({
  markdown,
  text,
  html
}: {
  markdown: string;
  text: string;
  html: string;
}) => void;
export type RTEProps = {
  text: string;
  onChange: TextEventHandlerT;
  shouldForceUpdateRTEAnswer?: boolean;
  setForceUpdateRTEAnswer?: (prevValue: boolean) => void;
  onBlur?: TextEventHandlerT;
  styles?: Record<string, string>;
  placeholder?: string;
  readOnly?: boolean;
  toolbar?: boolean;
  config?: {
    disablePaste?: boolean;
  };
};
const EditorContainer = styled('div', ({
  $theme
}) => ({
  boxSizing: 'border-box',
  borderRadius: $theme.borders.radius200,
  color: $theme.colors.primaryA,
  backgroundColor: $theme.colors.backgroundSecondary,
  position: 'relative',
  width: '100%',
  cursor: 'auto'
}));
const EditorInput = styled<'div', {
  $styles?: Record<string, string>;
}>('div', ({
  $styles
}: {
  $theme: ThemeT;
  $styles?: Record<string, string>;
}) => ({
  position: 'relative',
  overflow: 'auto',
  ...$styles
}));
const Placeholder = styled('div', ({
  $theme
}) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  userSelect: 'none',
  display: 'inline-block',
  pointerEvents: 'none',
  color: $theme.colors.contentTertiary,
  position: 'absolute',
  top: $theme.sizing.scale200,
  left: $theme.sizing.scale200
}));
const getContentEditableStyles = (theme: ThemeT): StyleObject => ({
  padding: theme.sizing.scale200,
  outline: 'none',
  minHeight: 'inherit',
  resize: 'vertical',
  overflow: 'auto',
  '.editor-input ul': {
    'list-style-type': 'disc'
  },
  '.editor-input ul ul': {
    'list-style-type': 'circle'
  },
  '.editor-input ul ul ul': {
    'list-style-type': 'square'
  },
  '.editor-input li[role="checkbox"]': {
    paddingLeft: '20px'
  },
  '.editor-input li[role="checkbox"]:before': {
    width: '12px',
    height: '12px'
  },
  ':focus': {
    outline: 0,
    boxShadow: `0 0 0 1px ${theme.colors.borderAccent} inset`
  }
});
const PLACEHOLDER = 'Enter text...';
export const LexicalRichTextEditor = ({
  text,
  onChange,
  shouldForceUpdateRTEAnswer,
  setForceUpdateRTEAnswer,
  onBlur,
  styles = {},
  placeholder = PLACEHOLDER,
  readOnly = false,
  toolbar = true,
  config = {}
}: RTEProps) => {
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLElement | null>(null);
  const {
    editorConfig
  } = useEditorConfig({
    initialText: text,
    readOnly
  });
  const [css, theme] = useStyletron();
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);
  const Editable = readOnly ? ReadOnlyContentEditable : ContentEditable;
  // we can not import inline css in fractal and for Katex support we need to load css from CDN
  useLoadStyles(true);
  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };
  return <LexicalComposer initialConfig={editorConfig}>
      <EditorContainer data-testid="lexical-editor-container">
        {toolbar && <ToolbarPlugin setIsLinkEditMode={setIsLinkEditMode} />}
        <EditorInput $styles={styles} className="editor-input">
          <RichTextPlugin contentEditable={<div ref={onRef} className={css({
          minHeight: 'inherit'
        })}>
                <Editable className={css(getContentEditableStyles(theme))} />
              </div>} placeholder={<Placeholder>{placeholder}</Placeholder>} ErrorBoundary={LexicalErrorBoundary} />
          <HistoryPlugin />
          <CustomOnChangePlugin onChange={onChange} />
          {onBlur && <OnBlurPlugin onBlur={onBlur} />}
          <LinkPlugin validateUrl={validateUrl} />
          {floatingAnchorElem && <>
              <LinkEditorPlugin anchorElem={floatingAnchorElem} isLinkEditMode={isLinkEditMode} setIsLinkEditMode={setIsLinkEditMode} />
              <CodeLanguagePlugin anchorElem={floatingAnchorElem} />
            </>}
          <CodeHighlightPlugin />
          <LexicalAutoLinkPlugin />
          <ListPlugin />
          <TabIndentationPlugin />
          <MarkdownShortcutPlugin transformers={EDITOR_TRANSFORMER} />
          <CheckListPlugin />
          <EquationsPlugin />
          {shouldForceUpdateRTEAnswer && <TextSyncPlugin text={text} setForceUpdateRTEAnswer={setForceUpdateRTEAnswer} />}
          <PastePlugin shouldBlockPaste={config?.disablePaste} />
        </EditorInput>
        <MetaDetailsPlugin />
      </EditorContainer>
    </LexicalComposer>;
};